// ProductoDetalle (tienda v5) — port de maquetas/tienda-v5-producto-detalle.html a JSX.
// Página 6 de Fase 3. Ruta dinámica /producto/:slug (el valor es el SKU). SKU inválido → 404.
//
// Sub-fase 4.0: datos REALES desde public.productos vía src/lib/tiendaApi (fetch cacheado,
// compartido con Catalogo). Galería con foto real (imagen_url + etiquetas_urls). Ficha real
// (descripción, beneficios, specs, atributos). Loading/error con el shell de la página.
//
// Reusa shared: TopNav(gold) / Breadcrumb / Footer / BottomNav / MiniBar / PrecioBox /
// PagaCon(transferencia) / FaqMini / CrossLinkBanner. Solo aporta el CSS del hero-split,
// galería, ficha, relacionados, envío y toast.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TopNav, Breadcrumb, Footer, BottomNav, MiniBar, PrecioBox, PagaCon, FaqMini, CrossLinkBanner, QtySelector, ScrollHintPill } from '../../components/tienda-v5';
import { TV5_ROUTES } from '../../components/tienda-v5/routes';
import { fetchProductosTienda, categoriaLabel, categoriaEmoji } from '../../lib/tiendaApi';
import { useCart } from '../../lib/CartContext';
import { trackEvent } from '../../lib/metaPixel';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');
const catLabel = (key) => categoriaLabel(key);

// LimpackFlex — "llega hoy / mañana / el lunes" según el corte, mismo criterio que la función
// SQL oneshot_fecha_entrega (flex 16h lun-vie · sábado 10h · domingo→lunes). Se recalcula en cada visita.
function cuandoFlex(d) {
  const day = d.getDay();
  const h = d.getHours() + d.getMinutes() / 60;
  if (day === 0) return { p: 'el lunes', hoy: false, c: 'Domingo no repartimos — llega el lunes.' };
  if (day === 6) return h < 10
    ? { p: 'hoy', hoy: true, c: 'Sábado: comprá antes de las 10 h y llega hoy.' }
    : { p: 'el lunes', hoy: false, c: 'Sábado pasadas las 10 h — llega el lunes.' };
  return h < 16
    ? { p: 'hoy', hoy: true, c: 'Comprá antes de las 16 h y llega hoy.' }
    : { p: 'mañana', hoy: false, c: 'Ya pasó el corte de las 16 h — llega mañana.' };
}

// Catálogo v5: hoy vive en /dev (la ruta prod /tienda/catalogo aún sirve la tienda vieja).
// Sub-fase 4.5 (merge) → cambiar a TV5_ROUTES.tienda. Se usa en el CTA "Seguir comprando".
const CATALOGO_V5_HREF = '/dev/tienda-v5-catalogo';

// FAQ genérica de tienda (política real del proyecto — no inventa nada del producto puntual).
const FAQ_PRODUCTO = [
  { q: '¿Cuándo me llega?', a: <>Con <b>LimpackFlex</b> llega el mismo día en Montevideo y Ciudad de la Costa si comprás antes de las 16 h (después, al día siguiente). Envío <b>gratis desde $1.000</b>. Al interior del país despachamos a Terminal Tres Cruces.</> },
  { q: '¿Lo puedo devolver?', a: <>Sí. Por la <b>Ley 17.250</b> tenés 5 días hábiles para arrepentirte. Si viene fallado, el cambio es inmediato y sin cargo.</> },
  { q: '¿Necesito suscribirme?', a: <>No. Comprás suelto lo que quieras. La suscripción es opcional y te da envío gratis <b>siempre</b> el día de tu barrio.</> },
  { q: '¿Cómo pago?', a: <>Con MercadoPago (tarjeta, débito o dinero en cuenta) o por transferencia bancaria. Todo con cifrado SSL.</> },
];

// ── 404 amigable ──
function ProductoNoEncontrado({ slug }) {
  return (
    <div className="tv5-page-producto">
      <style>{PRODUCTO_CSS}</style>
      <TopNav active="tienda" accent="gold" />
      <main>
        <div className="pd-404">
          <div className="pd-404-emoji">🧹</div>
          <h1>No encontramos ese producto</h1>
          <p>El producto <code>{slug}</code> no existe o ya no está disponible.</p>
          <Link to={TV5_ROUTES.tienda} className="pd-404-btn">← Volver a la Tienda</Link>
        </div>
      </main>
      <Footer accent="gold" />
      <BottomNav active="tienda" />
    </div>
  );
}

// ── Loading / error con el shell de la página ──
function ProductoEstado({ variante, onRetry }) {
  return (
    <div className="tv5-page-producto">
      <style>{PRODUCTO_CSS}</style>
      <TopNav active="tienda" accent="gold" />
      <main>
        {variante === 'error' ? (
          <div className="pd-404">
            <div className="pd-404-emoji">😕</div>
            <h1>No pudimos cargar el producto</h1>
            <p>Puede ser la conexión. Probá de nuevo.</p>
            <button className="pd-404-btn" onClick={onRetry}>Reintentar</button>
          </div>
        ) : (
          <section className="pd-hero" aria-busy="true">
            <div className="pd-gallery">
              <div className="pd-gallery-main pd-skel" />
            </div>
            <div className="pd-info">
              <div className="pd-skel pd-skel-line" style={{ width: '40%' }} />
              <div className="pd-skel pd-skel-line" style={{ width: '80%', height: 28 }} />
              <div className="pd-skel pd-skel-line" style={{ width: '55%' }} />
              <div className="pd-skel pd-skel-box" />
            </div>
          </section>
        )}
      </main>
      <Footer accent="gold" />
      <BottomNav active="tienda" />
    </div>
  );
}

export default function ProductoDetalle() {
  const { slug: sku } = useParams();
  const [productos, setProductos] = useState(null);
  const [error, setError] = useState(false);

  const cargar = useCallback(() => {
    let alive = true;
    setError(false);
    setProductos(null);
    fetchProductosTienda()
      .then((list) => { if (alive) setProductos(list); })
      .catch((e) => { if (alive) { console.error('[ProductoDetalle] fetch', e); setError(true); } });
    return () => { alive = false; };
  }, []);
  useEffect(() => cargar(), [cargar]);

  if (error) return <ProductoEstado variante="error" onRetry={cargar} />;
  if (productos == null) return <ProductoEstado variante="loading" />;
  const p = productos.find((x) => x.sku === sku);
  if (!p) return <ProductoNoEncontrado slug={sku} />;
  return <ProductoDetalleView p={p} todos={productos} />;
}

function ProductoDetalleView({ p, todos }) {
  // Galería: foto principal (imagen_url) + etiquetas (fotos extra del bucket). Fallback emoji.
  const fotos = useMemo(() => [p.imagen_url, ...(p.etiquetas_urls || [])].filter(Boolean), [p]);
  const [thumbIdx, setThumbIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const flexCuando = cuandoFlex(new Date());

  // Meta Pixel: ViewContent con el SKU (empareja la visita con el producto del catálogo).
  useEffect(() => {
    if (!p?.sku) return;
    trackEvent('ViewContent', {
      content_ids: [p.sku],
      content_type: 'product',
      content_name: p.nombre,
      value: Number(p.precio) || 0,
      currency: 'UYU',
    });
  }, [p?.sku, p?.nombre, p?.precio]);

  // Agregar al carrito real (CartContext) + toast + bump del MiniBar. `agregado` habilita el CTA
  // "← Seguir comprando" (Sub-fase 4.6, opción C: post-agregado, volver fácil al catálogo).
  const { addItem } = useCart();
  const [toast, setToast] = useState('');
  const [bumpSignal, setBumpSignal] = useState(0);
  const [agregado, setAgregado] = useState(false);
  const toastTimer = useRef(null);
  function agregar() {
    addItem(p, qty);
    setToast(`✓ Agregado al carrito (${qty})`);
    setBumpSignal((n) => n + 1);
    setAgregado(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2200);
  }
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Relacionados: primero misma categoría (excluye el actual), luego el resto. 4 como <Link> reales.
  const relacionados = useMemo(() => {
    const otros = todos.filter((x) => x.sku !== p.sku);
    const misma = otros.filter((x) => x.categoria === p.categoria);
    const resto = otros.filter((x) => x.categoria !== p.categoria);
    return [...misma, ...resto].slice(0, 4);
  }, [p, todos]);

  // Specs de ficha (solo las que existen) + atributos booleanos.
  const specs = useMemo(() => [
    ['Aroma', p.aroma], ['Rendimiento', p.rendimiento], ['Modo de uso', p.modo_uso],
    ['Ideal para', p.ideal_para], ['Composición', p.composicion],
  ].filter(([, v]) => v && String(v).trim()), [p]);
  const atributos = useMemo(() => [
    p.hipoalergenico && 'Hipoalergénico', p.biodegradable && 'Biodegradable',
    p.apto_ninos_mascotas && 'Apto niños y mascotas', p.sin_fragancia && 'Sin fragancia',
  ].filter(Boolean), [p]);
  const tieneFicha = !!(p.descripcion || p.beneficios.length || specs.length || atributos.length);

  const pct = p.salePct;
  const galleryBadge = p.sale ? `SALE ${p.sale}` : p.badge === 'nuevo' ? 'Nuevo' : '✓ Chequeado';

  return (
    <div className="tv5-page-producto">
      <style>{PRODUCTO_CSS}</style>

      <TopNav active="tienda" accent="gold" />

      <Breadcrumb
        backHref={TV5_ROUTES.tienda}
        backLabel="Volver"
        trail={[{ label: 'Tienda', href: TV5_ROUTES.tienda }, { label: catLabel(p.categoria), href: `${CATALOGO_V5_HREF}?cat=${p.categoria}` }]}
        current={p.nombre}
      />

      <main>
        {/* HERO SPLIT: galería + info */}
        <section className="pd-hero">
          <div className="pd-gallery">
            <div className="pd-gallery-main">
              <span className="pd-gallery-badge">{galleryBadge}</span>
              {fotos.length > 0
                ? <img className="pd-foto-main" src={fotos[thumbIdx]} alt={p.nombre} />
                : <span className="pd-icon-big">{categoriaEmoji(p.categoria)}</span>}
            </div>
            {fotos.length > 1 && (
              <div className="pd-thumbs">
                {fotos.map((t, i) => (
                  <button
                    key={i}
                    className={'pd-thumb' + (i === thumbIdx ? ' activo' : '')}
                    aria-label={`Vista ${i + 1}`}
                    aria-pressed={i === thumbIdx}
                    onClick={() => setThumbIdx(i)}
                  ><img src={t} alt="" loading="lazy" /></button>
                ))}
              </div>
            )}
          </div>

          <div className="pd-info">
            {p.marca && (
              <div className="pd-marca">
                <span className="pd-marca-logo">{p.marca[0]}</span>
                {p.marca}
              </div>
            )}
            <h1>{p.nombre}</h1>
            <div className="pd-sku">SKU · {p.sku}</div>
            <p className="pd-desc-corta">
              {p.descripcion?.trim()
                ? p.descripcion
                : `${p.presentacion} · ${catLabel(p.categoria)}. Seleccionado y chequeado por el equipo de Limpack.`}
            </p>

            <PrecioBox
              precio={p.precio}
              unidad={`· ${p.presentacion}`}
              nota={
                <>
                  {p.tachado && (
                    <div className="pd-antes">Antes <span className="pd-tachado">{fmt(p.tachado)}</span> · <strong className="pd-desc-pct">{p.sale || `-${pct}%`} esta semana</strong></div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--tv5pd-dim)', marginTop: 4 }}>Precio con IVA incluido</div>
                </>
              }
            />

            {/* Módulo LimpackFlex — envío en el día, dinámico por el corte (tipo ML). Pegado al precio. */}
            <div className="pd-flexmod">
              <span className="pd-flex-chip"><span className="pd-bolt">⚡</span> LimpackFlex · Envío en el día</span>
              <div className="pd-flex-llega">Llega <span style={{ color: flexCuando.hoy ? '#10B981' : '#5BBDD8' }}>{flexCuando.p}</span> en Montevideo y Canelones</div>
              <p className="pd-flex-sub"><b>Gratis desde $1.000.</b> {flexCuando.c}</p>
              <div className="pd-flex-note">⭐ Suscriptores: envío gratis siempre.</div>
            </div>

            <PagaCon transferencia={true} />

            <div className="pd-qty-row">
              <QtySelector value={qty} min={1} max={99} onChange={setQty} />
              <button className="pd-btn-carrito" onClick={agregar}>🛒 Agregar al carrito</button>
            </div>

            {/* Post-agregado: volver fácil al catálogo (opción C). El MiniBar ya ofrece "Ir a pagar". */}
            {agregado && (
              <Link to={CATALOGO_V5_HREF} className="pd-seguir-comprando">← Seguir comprando</Link>
            )}

            <div className="pd-quick">
              <div className="pd-quick-item">
                <span className="qi">{p.agotado ? '⏳' : '✓'}</span>{p.agotado ? 'Sin stock por ahora' : 'Stock disponible'}
              </div>
              <div className="pd-quick-item"><span className="qi">✓</span>Cambio sin cargo</div>
              <div className="pd-quick-item"><span className="qi">✓</span>Producto chequeado</div>
            </div>
          </div>
        </section>

        {/* FICHA: descripción real, beneficios, specs y atributos (solo lo que existe) */}
        {tieneFicha && (
          <section className="pd-seccion">
            <h2>Sobre el <em>producto.</em></h2>
            <div className="pd-ficha">
              {p.beneficios.length > 0 && (
                <ul className="pd-benef">
                  {p.beneficios.map((b, i) => <li key={i}><span className="pd-benef-check">✓</span>{b}</li>)}
                </ul>
              )}
              {specs.length > 0 && (
                <dl className="pd-specs">
                  {specs.map(([k, v]) => (
                    <div className="pd-spec" key={k}><dt>{k}</dt><dd>{v}</dd></div>
                  ))}
                </dl>
              )}
              {atributos.length > 0 && (
                <div className="pd-atributos">
                  {atributos.map((a) => <span className="pd-atributo" key={a}>{a}</span>)}
                </div>
              )}
            </div>
          </section>
        )}

        {/* PREGUNTAS RÁPIDAS */}
        <section className="pd-seccion">
          <h2>Preguntas <em>rápidas.</em></h2>
          <FaqMini items={FAQ_PRODUCTO} verTodasHref={TV5_ROUTES.faq} />
        </section>

        {/* CROSS-SELL a kits */}
        <section className="pd-seccion">
          <CrossLinkBanner
            accent="gold"
            title="¿Lo usás todos los meses?"
            sub="Suscribite a un kit y te llega solo, con envío gratis el día de tu barrio. Sale más barato que comprarlo suelto."
            ctaLabel="Ver los kits →"
            ctaHref={TV5_ROUTES.kits}
          />
        </section>

        {/* RELACIONADOS */}
        <section className="pd-seccion">
          <h2>Se compran <em>juntos.</em></h2>
          <p className="pd-seccion-sub">Lo que la gente suele agregar al carrito junto con esto.</p>
          <div className="pd-relac-grid">
            {relacionados.map((r) => (
              <Link key={r.sku} to={`${TV5_ROUTES.producto}/${r.sku}`} className="pd-relac-card">
                <div className="pd-relac-img">
                  {r.imagen_url
                    ? <img src={r.imagen_url} alt={r.nombre} loading="lazy" />
                    : <span>{categoriaEmoji(r.categoria)}</span>}
                </div>
                <div className="pd-relac-marca">{r.marca || catLabel(r.categoria)}</div>
                <div className="pd-relac-nombre">{r.nombre}</div>
                <div className="pd-relac-precio">{fmt(r.precio)}</div>
              </Link>
            ))}
          </div>
        </section>

      </main>

      {/* Toast feedback "Agregar al carrito" */}
      <div className={'pd-toast' + (toast ? ' visible' : '')} role="status" aria-live="polite">{toast}</div>

      <ScrollHintPill />
      <MiniBar checkoutHref={TV5_ROUTES.checkout} bumpSignal={bumpSignal} />
      <Footer accent="gold" />
      <BottomNav active="tienda" />
    </div>
  );
}

const PRODUCTO_CSS = `
.tv5-page-producto {
  --tv5pd-navy:#1A3461; --tv5pd-teal:#00A8B5; --tv5pd-gold:#C5A059; --tv5pd-green:#10B981;
  --tv5pd-bg:#0A1424; --tv5pd-card:rgba(255,255,255,0.04); --tv5pd-border:rgba(255,255,255,0.08);
  --tv5pd-text:#F8F6F1; --tv5pd-muted:rgba(248,246,241,0.65); --tv5pd-dim:rgba(248,246,241,0.45);
  --safe-bottom:env(safe-area-inset-bottom, 0px);
  background:var(--tv5pd-bg); color:var(--tv5pd-text); min-height:100vh; position:relative;
  overflow-x:clip; max-width:100%;
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
  padding-bottom:calc(72px + var(--safe-bottom));
}
.tv5-page-producto::before {
  content:''; position:fixed; inset:0; pointer-events:none; z-index:0;
  background:
    radial-gradient(circle at 20% 10%, rgba(0,168,181,0.10) 0%, transparent 40%),
    radial-gradient(circle at 80% 60%, rgba(197,160,89,0.08) 0%, transparent 40%);
}
.tv5-page-producto main { position:relative; z-index:1; }
@media (min-width:900px){ .tv5-page-producto { padding-bottom:0; } }

/* HERO SPLIT */
.tv5-page-producto .pd-hero { max-width:1200px; margin:20px auto 0; padding:20px; display:grid; grid-template-columns:1fr; gap:24px; }
@media (min-width:900px){ .tv5-page-producto .pd-hero { grid-template-columns:1fr 1fr; gap:40px; padding:30px 20px; align-items:start; } }

/* Galería */
.tv5-page-producto .pd-gallery { position:relative; }
.tv5-page-producto .pd-gallery-main {
  aspect-ratio:1 / 1; border-radius:24px;
  background:linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(0,168,181,0.08) 100%);
  border:1px solid var(--tv5pd-border); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
  display:flex; align-items:center; justify-content:center; position:relative; overflow:hidden;
}
.tv5-page-producto .pd-icon-big { font-size:160px; filter:drop-shadow(0 20px 40px rgba(0,0,0,0.4)); line-height:1; }
/* Mobile: 1:1 empujaba precio+CTA fuera de pantalla → 4:3 / máx 300px (Fable) */
@media (max-width:640px){
  .tv5-page-producto .pd-gallery-main { aspect-ratio:4 / 3; max-height:300px; }
  .tv5-page-producto .pd-icon-big { font-size:116px; }
}
.tv5-page-producto .pd-gallery-badge {
  position:absolute; top:20px; left:20px; z-index:2;
  background:rgba(0,168,181,0.9); backdrop-filter:blur(10px);
  color:#fff; font-size:11px; font-weight:800; letter-spacing:0.08em; text-transform:uppercase;
  padding:8px 14px; border-radius:999px;
}
.tv5-page-producto .pd-thumbs { display:flex; gap:10px; margin-top:14px; overflow-x:auto; scrollbar-width:none; }
.tv5-page-producto .pd-thumbs::-webkit-scrollbar { display:none; }
.tv5-page-producto .pd-thumb {
  width:72px; height:72px; border-radius:14px; flex-shrink:0;
  background:rgba(255,255,255,0.06); border:1px solid var(--tv5pd-border);
  display:flex; align-items:center; justify-content:center; font-size:32px; cursor:pointer;
  transition:all 0.2s; color:inherit; -webkit-tap-highlight-color:transparent;
}
.tv5-page-producto .pd-thumb.activo { border-color:var(--tv5pd-teal); background:rgba(0,168,181,0.12); }

/* Info */
.tv5-page-producto .pd-info { display:flex; flex-direction:column; gap:12px; }
.tv5-page-producto .pd-marca { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--tv5pd-muted); font-weight:700; letter-spacing:0.08em; text-transform:uppercase; }
.tv5-page-producto .pd-marca-logo { width:24px; height:24px; border-radius:6px; background:linear-gradient(135deg, var(--tv5pd-teal), var(--tv5pd-navy)); display:flex; align-items:center; justify-content:center; color:#fff; font-size:11px; font-weight:900; }
.tv5-page-producto .pd-info h1 { font-size:clamp(26px, 5vw, 36px); font-weight:800; letter-spacing:-0.02em; line-height:1.15; }
.tv5-page-producto .pd-sku { font-size:12px; color:var(--tv5pd-dim); }
.tv5-page-producto .pd-desc-corta { font-size:14px; color:var(--tv5pd-muted); line-height:1.55; margin-top:6px; }

/* Notas del PrecioBox (slot nota): descuento + suscriptor */
.tv5-page-producto .pd-antes { font-size:12px; color:var(--tv5pd-muted); margin-bottom:8px; }
.tv5-page-producto .pd-tachado { text-decoration:line-through; color:var(--tv5pd-dim); }
.tv5-page-producto .pd-desc-pct { color:#FFD700; }
.tv5-page-producto .pd-suscriptor { display:flex; align-items:center; gap:8px; flex-wrap:wrap; font-size:13px; color:var(--tv5pd-teal); font-weight:700; }
.tv5-page-producto .pd-suscriptor-badge { background:rgba(0,168,181,0.14); border:1px solid rgba(0,168,181,0.35); padding:4px 10px; border-radius:999px; }

/* Cantidad + CTA */
.tv5-page-producto .pd-qty-row { display:flex; align-items:center; gap:10px; margin-top:14px; }
.tv5-page-producto .pd-btn-carrito {
  flex:1; display:flex; align-items:center; justify-content:center; gap:8px;
  background:linear-gradient(135deg, var(--tv5pd-teal) 0%, #00C4D4 100%); color:#0A1424;
  padding:14px 22px; border-radius:12px; font-weight:800; font-size:15px;
  text-decoration:none; box-shadow:0 10px 28px rgba(0,168,181,0.35); cursor:pointer; border:0;
  min-height:44px; transition:transform 0.2s;
}
@media (hover:hover){ .tv5-page-producto .pd-btn-carrito:hover { transform:translateY(-2px); } }
.tv5-page-producto .pd-btn-carrito:active { transform:translateY(-2px); }

/* CTA "← Seguir comprando" (aparece post-agregado) */
.tv5-page-producto .pd-seguir-comprando {
  display:flex; align-items:center; justify-content:center; gap:6px; margin-top:10px;
  padding:12px 18px; border-radius:12px; min-height:44px;
  background:rgba(197,160,89,0.12); border:1px solid rgba(197,160,89,0.4); color:var(--tv5pd-gold);
  font-size:14px; font-weight:800; text-decoration:none;
  animation:pdSeguirIn 0.3s ease; transition:background 0.2s, transform 0.2s;
}
@keyframes pdSeguirIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
@media (hover:hover){ .tv5-page-producto .pd-seguir-comprando:hover { background:rgba(197,160,89,0.2); transform:translateY(-1px); } }
@media (prefers-reduced-motion:reduce){ .tv5-page-producto .pd-seguir-comprando { animation:none; } }

/* Bullets info rápida */
.tv5-page-producto .pd-flexmod { margin-top:14px; background:linear-gradient(135deg, rgba(0,168,181,0.13), rgba(0,168,181,0.03)); border:1px solid rgba(0,168,181,0.32); border-radius:14px; padding:14px; }
@keyframes pdGlow { 0%,100% { box-shadow:0 0 0 0 rgba(0,168,181,0); } 50% { box-shadow:0 0 14px 1px rgba(0,168,181,0.4); } }
@keyframes pdBlink { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
.tv5-page-producto .pd-flex-chip { display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; color:var(--tv5pd-teal); background:rgba(0,168,181,0.14); border:1px solid rgba(0,168,181,0.34); padding:3px 9px; border-radius:999px; margin-bottom:8px; animation:pdGlow 2.2s ease-in-out infinite; }
.tv5-page-producto .pd-bolt { display:inline-block; animation:pdBlink 1s steps(1,end) infinite; }
.tv5-page-producto .pd-flex-llega { font-size:16px; font-weight:800; letter-spacing:-0.01em; color:var(--tv5pd-text); }
.tv5-page-producto .pd-flex-sub { font-size:12px; color:var(--tv5pd-muted); margin:2px 0 0; line-height:1.45; }
.tv5-page-producto .pd-flex-sub b { color:var(--tv5pd-text); }
.tv5-page-producto .pd-flex-note { margin-top:9px; font-size:11.5px; color:var(--tv5pd-muted); }
.tv5-page-producto .pd-quick { display:grid; grid-template-columns:repeat(2, 1fr); gap:8px; margin-top:16px; padding-top:16px; border-top:1px solid var(--tv5pd-border); }
.tv5-page-producto .pd-quick-item { display:flex; align-items:center; gap:8px; font-size:12px; color:var(--tv5pd-muted); }
.tv5-page-producto .pd-quick-item .qi { color:var(--tv5pd-teal); font-weight:800; font-size:14px; }

/* SECCIONES */
.tv5-page-producto .pd-seccion { max-width:1200px; margin:50px auto 0; padding:0 20px; }
.tv5-page-producto .pd-seccion h2 { font-size:clamp(22px, 3.5vw, 28px); font-weight:800; letter-spacing:-0.02em; margin-bottom:8px; }
.tv5-page-producto .pd-seccion h2 em { color:var(--tv5pd-teal); font-style:italic; }
.tv5-page-producto .pd-seccion-sub { font-size:14px; color:var(--tv5pd-muted); line-height:1.5; margin-bottom:20px; }

/* Relacionados */
.tv5-page-producto .pd-relac-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:10px; }
@media (min-width:640px){ .tv5-page-producto .pd-relac-grid { grid-template-columns:repeat(4, 1fr); gap:14px; } }
.tv5-page-producto .pd-relac-card {
  background:var(--tv5pd-card); border:1px solid var(--tv5pd-border); border-radius:16px; padding:16px 14px;
  text-decoration:none; color:inherit; transition:all 0.2s; position:relative; overflow:hidden;
  display:flex; flex-direction:column;
}
@media (hover:hover){ .tv5-page-producto .pd-relac-card:hover { border-color:rgba(0,168,181,0.35); transform:translateY(-3px); background:rgba(255,255,255,0.06); } }
.tv5-page-producto .pd-relac-img { aspect-ratio:1 / 1; background:rgba(255,255,255,0.04); border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:44px; margin-bottom:10px; }
.tv5-page-producto .pd-relac-marca { font-size:10px; color:var(--tv5pd-dim); letter-spacing:0.05em; text-transform:uppercase; font-weight:600; margin-bottom:6px; }
.tv5-page-producto .pd-relac-nombre { font-size:13px; color:var(--tv5pd-text); font-weight:700; line-height:1.3; margin-bottom:4px; }
.tv5-page-producto .pd-relac-precio { font-size:16px; color:var(--tv5pd-text); font-weight:900; letter-spacing:-0.01em; margin-top:auto; }

/* Envío */
.tv5-page-producto .pd-envio-box { background:var(--tv5pd-card); border:1px solid var(--tv5pd-border); border-radius:20px; padding:24px; display:grid; grid-template-columns:1fr; gap:16px; }
@media (min-width:720px){ .tv5-page-producto .pd-envio-box { grid-template-columns:repeat(3, 1fr); } }
.tv5-page-producto .pd-envio-item { display:flex; gap:12px; align-items:flex-start; }
.tv5-page-producto .pd-envio-icon { font-size:26px; flex-shrink:0; }
.tv5-page-producto .pd-envio-item h4 { font-size:14px; font-weight:800; color:var(--tv5pd-text); margin-bottom:4px; letter-spacing:-0.01em; }
.tv5-page-producto .pd-envio-item p { font-size:12px; color:var(--tv5pd-muted); line-height:1.5; }
.tv5-page-producto .pd-envio-item p b { color:var(--tv5pd-text); }
.tv5-page-producto .pd-envio-item p a { color:var(--tv5pd-teal); text-decoration:none; font-weight:700; }

/* Toast "Agregado al carrito" */
.tv5-page-producto .pd-toast {
  position:fixed; bottom:calc(140px + var(--safe-bottom)); left:50%; transform:translateX(-50%) translateY(10px);
  background:rgba(16,185,129,0.96); color:#04361f; border-radius:999px;
  padding:12px 20px; font-size:13px; font-weight:800; z-index:130; white-space:nowrap;
  box-shadow:0 16px 40px rgba(0,0,0,0.5); opacity:0; pointer-events:none;
  transition:opacity 0.25s, transform 0.25s;
}
.tv5-page-producto .pd-toast.visible { opacity:1; transform:translateX(-50%) translateY(0); }
@media (min-width:900px){ .tv5-page-producto .pd-toast { bottom:90px; } }

/* Fotos reales (galería, thumbs, relacionados) */
.tv5-page-producto .pd-foto-main { position:absolute; inset:0; width:100%; height:100%; object-fit:contain; padding:16px; display:block; }
.tv5-page-producto .pd-thumb { overflow:hidden; padding:0; }
.tv5-page-producto .pd-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.tv5-page-producto .pd-relac-img img { width:100%; height:100%; object-fit:cover; display:block; border-radius:12px; }

/* FICHA (sobre el producto) */
.tv5-page-producto .pd-ficha { background:var(--tv5pd-card); border:1px solid var(--tv5pd-border); border-radius:20px; padding:24px; display:grid; gap:20px; }
.tv5-page-producto .pd-benef { list-style:none; display:grid; gap:10px; }
@media (min-width:640px){ .tv5-page-producto .pd-benef { grid-template-columns:repeat(2, 1fr); } }
.tv5-page-producto .pd-benef li { display:flex; gap:10px; align-items:flex-start; font-size:14px; color:var(--tv5pd-muted); line-height:1.45; }
.tv5-page-producto .pd-benef-check { color:var(--tv5pd-teal); font-weight:900; flex-shrink:0; }
.tv5-page-producto .pd-specs { display:grid; gap:0; border-top:1px solid var(--tv5pd-border); }
.tv5-page-producto .pd-spec { display:grid; grid-template-columns:130px 1fr; gap:12px; padding:12px 0; border-bottom:1px solid var(--tv5pd-border); }
.tv5-page-producto .pd-spec dt { font-size:12px; font-weight:800; color:var(--tv5pd-dim); text-transform:uppercase; letter-spacing:0.05em; }
.tv5-page-producto .pd-spec dd { font-size:14px; color:var(--tv5pd-text); line-height:1.5; }
.tv5-page-producto .pd-atributos { display:flex; flex-wrap:wrap; gap:8px; }
.tv5-page-producto .pd-atributo { font-size:12px; font-weight:700; color:var(--tv5pd-teal); background:rgba(0,168,181,0.12); border:1px solid rgba(0,168,181,0.3); padding:6px 12px; border-radius:999px; }

/* Skeleton loading */
.tv5-page-producto .pd-skel { background:linear-gradient(100deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.04) 70%); background-size:200% 100%; animation:pdSkel 1.2s ease-in-out infinite; border-radius:12px; }
.tv5-page-producto .pd-gallery-main.pd-skel { aspect-ratio:1 / 1; }
@media (max-width:640px){ .tv5-page-producto .pd-gallery-main.pd-skel { aspect-ratio:4 / 3; max-height:300px; } }
.tv5-page-producto .pd-skel-line { height:14px; margin-bottom:12px; border-radius:8px; }
.tv5-page-producto .pd-skel-box { height:180px; margin-top:8px; }
@keyframes pdSkel { from { background-position:200% 0; } to { background-position:-200% 0; } }
@media (prefers-reduced-motion:reduce){ .tv5-page-producto .pd-skel { animation:none; } }

/* 404 */
.tv5-page-producto .pd-404 { max-width:520px; margin:0 auto; padding:70px 24px; text-align:center; position:relative; z-index:1; }
.tv5-page-producto .pd-404-emoji { font-size:64px; margin-bottom:16px; }
.tv5-page-producto .pd-404 h1 { font-size:26px; font-weight:800; margin-bottom:10px; }
.tv5-page-producto .pd-404 p { font-size:14px; color:var(--tv5pd-muted); margin-bottom:22px; }
.tv5-page-producto .pd-404 code { color:var(--tv5pd-gold); background:rgba(255,255,255,0.05); padding:2px 6px; border-radius:5px; font-size:12px; }
.tv5-page-producto .pd-404-btn { display:inline-flex; align-items:center; gap:6px; background:linear-gradient(135deg, var(--tv5pd-gold), #D4B370); color:var(--tv5pd-navy); padding:12px 22px; border-radius:12px; font-weight:800; font-size:14px; text-decoration:none; min-height:44px; border:0; cursor:pointer; font-family:inherit; }

/* Reduced motion */
@media (prefers-reduced-motion:reduce){
  .tv5-page-producto .pd-thumb, .tv5-page-producto .pd-btn-carrito, .tv5-page-producto .pd-relac-card { transition:none; }
  .tv5-page-producto .pd-toast { transition:opacity 0.2s; }
}
`;
